<template>
  <v-form v-model="formValid">
    <PageHeader :title="$t('title')"  save @save="save" :valid='formValid' icon="mdi-pine-tree" >

      <template v-slot:buttons>
        <TakePhoto @click="saveImage">
          <v-btn color="primary" small dense class="ml-1"><v-icon dense class="mr-1">mdi-camera-outline</v-icon> <span class="hidden-sm-and-down">{{$t('photo')}}</span></v-btn>
        </TakePhoto>
      </template>


      <template v-slot:menu>
        <TakePhoto  :text="$t('imageInfo')" @click="saveImage">
          <AppMenuItem icon="mdi-camera-outline" :title="$t('Take Photos')" />
        </TakePhoto>
      </template>

      <v-container fluid>

        <v-row class="blue lighten-5">
          <v-col cols="12" sm="4" >
            <v-text-field
              v-model="identifier"
              :rules="identifierRules"
              :label="$t('Identifier')"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="4" >
            <v-autocomplete
              v-model="theSpecies"
              :items="species"
              :label="$t('species')"
              single-line
              :rules="[(v) => !!v || 'Required']"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="4" >
            <DateInput
              v-model="plantedAt"
              :label="$t('plantedAt')"
              :max="new Date().toISOString().substr(0, 10)"
            />
          </v-col>
        </v-row>

        <v-row class="green lighten-5">
          <v-col cols="12" sm="3" >
            <v-text-field v-model="height" :rules="[measurementRules]" :label="$t('height')" min=0 type='number' :hint="$t('inMeters')" suffix="m" />
          </v-col>
          <v-col cols="12" sm="3" >
            <v-text-field v-model="girth" :rules="[measurementRules]" :label="$t('girth')" min=0 type='number' :hint="$t('girth_hint')" suffix="cm" />
          </v-col>
          <v-col cols="12" sm="3" >
            <v-select v-model="health" :items="healthOptions" :label="$t('health')"></v-select>
          </v-col>
          <v-col cols="12" sm="3" v-if="health==='bad' || health==='dying'">
            <v-combobox
              v-model="healthDescription"
              :items="healthDescriptions"
              :label="$t('healthDescription')"
              :hint="$t('healthDescription')"
              single-line
              @change="setHealthDescription"
            ></v-combobox >
          </v-col>
        </v-row>

        <v-row class="orange lighten-5">
          <v-col cols="12">
            <RegionPicker v-model="region" />
          </v-col>
          <v-col cols="12" sm="3" >
            <LocationInput v-model="location" />
          </v-col>
        </v-row>


        <v-row class="blue-grey lighten-5">
          <v-col cols="12">
            <v-textarea
              :label="$t('notes')"
              v-model="note"
              auto-grow
            ></v-textarea>
          </v-col>
        </v-row>

      </v-container>

    </PageHeader>
  </v-form>
</template>


<script>
  import { v4 as uuidv4 } from 'uuid';
  import treeSpecies from '@/mixins/treeSpecies';

  import PageHeader from '@/components/PageHeader';
  import NameInput from '@/components/PageHeader';
  import RegionPicker from '@/components/RegionPicker';
  import TakePhoto from '@/components/TakePhoto';
  import AppMenuItem from '@/components/AppMenuItem';
  import LocationInput from '@/components/LocationInput';
  import DateInput from '@/components/DateInput';

  export default {

    components: { PageHeader, NameInput, RegionPicker, TakePhoto, AppMenuItem, LocationInput, DateInput },

    data: () => ({
      formValid: false,
      amount: 0,

      uuid: undefined,
      identifier: undefined,
      theSpecies: undefined,
      plantedAt: undefined,

      height: undefined,
      girth: undefined,
      crown: undefined,
      volume: undefined,

      health: undefined,
      healthDescription: undefined,

      region: undefined,
      location: undefined,

      note: undefined,

      healthOptions: [
        { value: 'good', text: 'Good' },
        { value: 'okay', text: 'Okay' },
        { value: 'bad', text: 'Bad' },
        { value: 'dying', text: 'Dying' },
      ],

      healthDescriptions: [
        'Eaten by cow',
        'Burnt',
        'Chopped',
        'Diseased',
        'Dry',
      ],

      identifierRules: [
        // v => !!v || 'Required',
        v => /^.{0,20}$/.test(v) || 'Must be less than 20 characters',
        v => (v || '').length==0 || /^\S/.test(v) || 'No spaces at start',
        v => (v || '').length==0 || /\S$/.test(v) || 'No spaces at end',
      ],


    }),

    computed: {
      fieldAgentId() { return this.$store.getters.settings.fieldAgentId; },
    },

    methods: {
      setHealthDescription() {

      },
      measurementRules(v) {
        let r = true;
        if (parseFloat(v) <= 0) { r = this.$t('minZero'); }
        if (parseFloat(v) > 100) { r = this.$t('maxExceeded'); }
        return r;
      },
      saveImage(uuid) { this.$store.dispatch('addImage', { type: 'tree', type_id: this.uuid, uuid: uuid }); },
      save() {

        const tree = {
          uuid: this.uuid,
          createdAt: new Date().toISOString(),

          identifier: this.identifier,
          region: this.region,
          species: this.theSpecies,
          plantedAt: this.plantedAt,

          height: this.height,
          girth: this.girth,
          crown: this.crown,
          volume: this.volume,

          health: this.health,
          healthDescription: this.healthDescription,

          location: this.location,
        }

        this.$store.dispatch('addTree', tree);

        this.$router.push({ name: 'Trees' });
      },
    },

    mounted() {
      this.uuid = uuidv4();
      this.identifier = this.uuid.substring(0,5);
    },

    mixins: [treeSpecies],

  }
</script>


<i18n>
{
  "en": {
    "title": "New Tree",
    "Identifier": "Identifier",
    "plantedAt": "Planted",
    "girth_hint": "Girth at 1.3m above ground (DBH). cm.",
    "inMeters": "In meters",
    "health": "Health",
    "healthDescription": "Ill health, reason",
    "notes": "Notes"
  },
  "es": {
    "title": "Árbol nuevo",
    "Identifier": "Identificador",
    "plantedAt": "Plantada",
    "girth_hint": "Circunferencia a 1.3 m sobre el suelo (DBH). Centímetros.",
    "inMeters": "En metros",
    "health": "Salud",
    "healthDescription": "Mala salud, razon",
    "notes": "Notas"
  },
  "sw": {
    "title": "Mti Mpya",
    "Identifier": "Kitambulisho",
    "plantedAt": "Kupandwa",
    "girth_hint": "Girth kwa 1.3m juu ya ardhi (DBH). Sentimita.",
    "inMeters": "Katika mita",
    "health": "Afya",
    "healthDescription": "Afya mbaya, sababu",
    "notes": "Vidokezo"
  }
}
</i18n>
